// Visual primitive for the mode-level urgency indicator (D1).
//
// Renders nothing when the resolver returns `{ active: false }`. Otherwise
// overlays a small pulsing dot with a tone-coloured glow on the top-right of
// the mode chip. Motion-reduced users see the same dot but static (no pulse,
// no glow). Color is never the only signal: an `aria-label` + `title` are
// always present.
//
// Positioning: applies `absolute -top-0.5 -right-0.5` so it expects to be
// mounted inside a parent with `relative` (the mode chip span on both
// `SidebarModeList` and `MobileBottomTabBar`).
//
// Precedent: amber pulse pattern in
// `src/components/lead/sections/SalesDailyChecklistWidget.tsx` lines 50–67.

import { useModeUrgency } from "@/hooks/useModeUrgency";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import { KPI_TONE_CLASSES, KPI_TONE_DOT_CLASS, type KpiTone } from "@/components/common/kpi/kpiTypes";

export type ModeUrgencyBadgeSize = "rail" | "mobile";

export type ModeUrgencyBadgeProps = {
  mode: PrimaryMode;
  currentMode: PrimaryMode;
  size?: ModeUrgencyBadgeSize;
  /** Suppress the badge on the user's currently-active mode (default `true`). */
  hideOnActive?: boolean;
};

const SIZE_CLASSES: Record<ModeUrgencyBadgeSize, string> = {
  rail: "h-2.5 w-2.5",
  mobile: "h-2 w-2",
};

// Tone-specific glow shadows. The doctrine-locked visual treatment uses a
// soft, large-radius shadow to read as "alive" without screaming. The
// `motion-reduce:shadow-none` modifier below strips this when reduced motion
// is requested so the dot ends up plain coloured + ringed (still distinct).
const GLOW_BY_TONE: Record<KpiTone, string> = {
  amber: "shadow-[0_0_10px_rgba(251,191,36,0.55)] dark:shadow-[0_0_12px_rgba(251,191,36,0.45)]",
  red: "shadow-[0_0_10px_rgba(239,68,68,0.55)] dark:shadow-[0_0_12px_rgba(239,68,68,0.45)]",
  green: "shadow-[0_0_10px_rgba(16,185,129,0.55)] dark:shadow-[0_0_12px_rgba(16,185,129,0.45)]",
  gold: "shadow-[0_0_10px_rgba(234,179,8,0.55)] dark:shadow-[0_0_12px_rgba(234,179,8,0.45)]",
  grey: "shadow-[0_0_10px_rgba(156,163,175,0.45)] dark:shadow-[0_0_12px_rgba(156,163,175,0.40)]",
};

export function ModeUrgencyBadge({
  mode,
  currentMode,
  size = "rail",
  hideOnActive = true,
}: ModeUrgencyBadgeProps) {
  const urgency = useModeUrgency(mode);
  if (!urgency.active) return null;
  if (hideOnActive && currentMode === mode) return null;

  const tone = urgency.tone;
  const className = [
    "pointer-events-none absolute -top-0.5 -right-0.5 inline-block rounded-full",
    SIZE_CLASSES[size],
    KPI_TONE_DOT_CLASS[tone],
    "ring-2",
    KPI_TONE_CLASSES[tone].ring,
    "animate-pulse motion-reduce:animate-none motion-reduce:shadow-none",
    GLOW_BY_TONE[tone],
  ].join(" ");

  return (
    <span
      role="status"
      aria-label={urgency.ariaLabel}
      title={urgency.reason}
      className={className}
    />
  );
}
